import { pushActionCreator } from 'actions/navigationActions'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import {
  CC_ONBOARDING_STEP_ADDRESS,
  CC_ONBOARDING_STEP_DENIED,
  CC_ONBOARDING_STEP_OFFER,
  CC_ONBOARDING_STEP_DECLINED,
  CC_ONBOARDING_STEP_PENDING,
  CC_ONBOARDING_STEP_PHONE,
  CC_ONBOARDING_STEP_REVIEW,
  CC_ONBOARDING_STEP_SSN,
  CC_ONBOARDING_STEP_SUBMIT,
  CC_ONBOARDING_STEP_SUCCESS,
  CC_ONBOARDING_STATE_FINISHED,
} from 'components/features/creditCard/creditCardStore/constants'
import { ccOnboardingGuard } from 'components/features/creditCard/creditCardStore/guards/ccOnboardingGuard'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { FC, useEffect, useMemo } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'
import { AddressStepPage } from 'components/features/creditCard/creditCardOnboarding/AddressStepPage/AddressStepPage'
import { SSNStepPage } from 'components/features/creditCard/creditCardOnboarding/SSNStepPage/SSNStepPage'
import { ReviewStepPage } from 'components/features/creditCard/creditCardOnboarding/ReviewStepPage/ReviewStepPage'
import { PendingStepPage } from 'components/features/creditCard/creditCardOnboarding/PendingStepPage/PendingStepPage'
import { PhoneStepPage } from 'components/features/creditCard/creditCardOnboarding/PhoneStepPage/PhoneStepPage'
import { OfferStepPage } from 'components/features/creditCard/creditCardOnboarding/OfferStepPage/OfferStepPage'
import { ccStateGuard } from 'components/features/creditCard/creditCardStore/guards/ccStateGuard'
import { DeniedStepPage } from 'components/features/creditCard/creditCardOnboarding/DeniedStepPage/DeniedStepPage'
import { SuccessStepPage } from 'components/features/creditCard/creditCardOnboarding/SuccessStepPage/SuccessStepPage'
import { usePickStep } from 'components/features/creditCard/creditCardOnboarding/usePickStep'
import { DeclinedStepPage } from 'components/features/creditCard/creditCardOnboarding/DeclinedStepPage/DeclinedStepPage'
import { SubmittingStepPage } from 'components/features/creditCard/creditCardOnboarding/SubmittingStepPage/SubmittingStepPage'
import { GetCCOnboardingStateAction } from 'components/features/creditCard/creditCardStore/actions'

export const CC_ONBOARDING_PAGE_URL_PREFIX = '/credit-card/onboarding/'

type CreditCardOnboardingPageProps = {
}

export const CreditCardOnboardingPage: FC<CreditCardOnboardingPageProps> = (props) => {
  useSimpleGuard(ccStateGuard, props, 'CreditCardOnboardingPage')
  const isLoaded = useSimpleGuard(ccOnboardingGuard, props, 'CreditCardOnboardingPage')

  const { step } = useParams<{ step: string }>()
  const { path } = useRouteMatch()

  const onboarding = useStoreSelector((state) => state.data.creditCard.onboarding ?? null)
  const inferredStep = usePickStep(onboarding, step)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    if (inferredStep === null || inferredStep === step || !isLoaded || !onboarding) {
      return
    }

    dispatch(pushActionCreator({ pathname: `${ CC_ONBOARDING_PAGE_URL_PREFIX }${ inferredStep }`, push: false }))
  }, [dispatch, inferredStep, isLoaded, onboarding, path, step])

  useEffect(() => {
    if (onboarding?.state !== CC_ONBOARDING_STATE_FINISHED) {
      dispatch(new GetCCOnboardingStateAction())
    }
  }, [dispatch, onboarding?.state, step])

  const loadingChild = useMemo(() => {
    if (onboarding === null || !isLoaded) {
      return (
        <LoadingOverlay size='large' />
      )
    }
    return null
  }, [onboarding, isLoaded])

  const pageChild = useMemo(() => {
    switch (step) {
      case CC_ONBOARDING_STEP_ADDRESS: {
        return <AddressStepPage />
      }

      case CC_ONBOARDING_STEP_PHONE: {
        return <PhoneStepPage />
      }

      case CC_ONBOARDING_STEP_SSN: {
        return <SSNStepPage />
      }

      case CC_ONBOARDING_STEP_REVIEW: {
        return <ReviewStepPage />
      }

      case CC_ONBOARDING_STEP_PENDING: {
        return <PendingStepPage />
      }

      case CC_ONBOARDING_STEP_OFFER: {
        return <OfferStepPage />
      }

      case CC_ONBOARDING_STEP_SUBMIT: {
        return <SubmittingStepPage />
      }

      case CC_ONBOARDING_STEP_SUCCESS: {
        return <SuccessStepPage />
      }

      case CC_ONBOARDING_STEP_DECLINED: {
        return <DeclinedStepPage />
      }

      case CC_ONBOARDING_STEP_DENIED: {
        return <DeniedStepPage />
      }
    }
    return null
  }, [step])

  return (
    <>
      { loadingChild }
      { pageChild }
    </>
  )
}


